// DDT-3773 — hydrate the "Prochain RDV disponible" availability bubble on
// lawyer listing cards and profile pages.
//
// The page renders the bubble hidden (placeholder only). After load we collect
// every bubble's lawyer id, ask the theme endpoint (which proxies + caches the
// account app) for the next available date per lawyer, then format it in French
// and reveal the bubble. Lawyers without agenda sync / availability are simply
// never returned, so their bubble stays hidden.

import { formatNextSlotDate } from './page-date.mjs';

// DDT-4322 — A/B test: the variant turns each shown bubble into a link that opens the
// lawyer's contact form directly in the booking flow. The page HTML is the same in both
// arms; only the variant rewrites the bubbles client-side.
const EXPERIMENT_ID = 'ddt4322';

function pushGaEvent( action, category, label ) {
	window.dataLayer = window.dataLayer || [];
	window.dataLayer.push( {
		event: 'dataLayer.gaEvent',
		gaEvent: { action, category, label },
	} );
}

// Listing cards (standard, premium and the premium left column) are <article onclick="...">
// click handlers, not links; a bubble outside one is on a profile.
function isOnListingCard( el ) {
	return null !== el.closest( 'article[onclick]' );
}

function enableNextSlotExperiment() {
	let isVariant = false;

	if ( ! window.ddAbTest ) {
		return isVariant;
	}

	try {
		window.ddAbTest.enableExperiment( {
			id: EXPERIMENT_ID,
			action: EXPERIMENT_ID,
			sample: 100,
			variants: [
				{ id: 'control', weight: 50, activate: () => {} },
				{ id: 'variant', weight: 50, activate: () => { isVariant = true; } },
			],
		} );
	} catch ( error ) {
		// The experiment is optional; an A/B failure must not break the bubbles.
	}

	return isVariant;
}

function linkBubble( el ) {
	const url = el.getAttribute( 'data-booking-url' );

	if ( ! url || el.closest( 'a' ) ) {
		return;
	}

	const onListing = isOnListingCard( el );
	const link = document.createElement( 'a' );

	link.className = 'next-slot-link';
	link.href = url;
	link.rel = 'nofollow';
	link.dataset.contactSource = onListing ? 'listing next slot' : 'detail page next slot';

	// Same tab behaviour as the "Contacter" button of the page: new tab on desktop listing only.
	if ( onListing && el.hasAttribute( 'data-new-tab' ) ) {
		link.target = '_blank';
	}

	link.addEventListener( 'click', ( event ) => {
		// The listing card is a click handler, not a link: keep it from opening the profile.
		// The .stop-propagation class is bound at page load, so it would miss this link.
		event.stopPropagation();

		pushGaEvent(
			'nextslot_label_click',
			onListing ? 'listing results' : 'Detail',
			el.getAttribute( 'data-lawyer-id' )
		);
	} );

	const chevron = document.createElement( 'span' );
	chevron.className = 'next-slot-bubble__chevron ms-1';
	chevron.setAttribute( 'aria-hidden', 'true' );
	chevron.textContent = '›';
	el.appendChild( chevron );

	el.parentNode.insertBefore( link, el );
	link.appendChild( el );
}

function isVisible( el ) {
	return ! el.hidden && el.getClientRects().length > 0;
}

async function initNextSlotBubbles() {
	const bubbles = Array.from( document.querySelectorAll( '[data-next-slot][data-lawyer-id]' ) );

	if ( bubbles.length === 0 ) {
		return;
	}

	// Only bubbles with a booking URL take part in the test: BE and the recruitment listing render
	// none, so they get no arm, no link and no view event.
	const testBubbles = bubbles.filter( ( el ) => el.hasAttribute( 'data-booking-url' ) );
	const isVariant = testBubbles.length > 0 && enableNextSlotExperiment();

	const bubblesById = new Map();
	bubbles.forEach( ( el ) => {
		const id = el.getAttribute( 'data-lawyer-id' );
		if ( ! id ) {
			return;
		}
		if ( ! bubblesById.has( id ) ) {
			bubblesById.set( id, [] );
		}
		bubblesById.get( id ).push( el );
	} );

	const lawyerIds = Array.from( bubblesById.keys() );

	let dates = {};
	try {
		const response = await fetch( window.jfRestUrl( 'justifit/v1/next-slot' ), {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify( { lawyer_ids: lawyerIds } ),
		} );

		if ( ! response.ok ) {
			return;
		}

		const data = await response.json();
		dates = ( data && data.dates ) || {};
	} catch ( error ) {
		// Availability is non-critical decoration; fail silently.
		return;
	}

	Object.keys( dates ).forEach( ( id ) => {
		const formatted = formatNextSlotDate( dates[ id ], document.documentElement.lang, window.jfPublicText.first_day );
		if ( ! formatted ) {
			return;
		}

		( bubblesById.get( id ) || [] ).forEach( ( el ) => {
			const dateEl = el.querySelector( '.next-slot-bubble__date' );
			if ( dateEl ) {
				dateEl.textContent = formatted;
			}
			el.hidden = false;

			if ( isVariant ) {
				linkBubble( el );
			}
		} );
	} );

	// Sent in both arms, once per page view, so the analysis can select the sessions that saw
	// a label. Bubbles hidden by the layout (e.g. the other breakpoint's copy) are not counted.
	const visibleCount = testBubbles.filter( isVisible ).length;

	if ( visibleCount > 0 ) {
		pushGaEvent(
			'nextslot_label_view',
			testBubbles.some( isOnListingCard ) ? 'listing results' : 'Detail',
			String( visibleCount )
		);
	}
}

if ( document.readyState === 'loading' ) {
	document.addEventListener( 'DOMContentLoaded', initNextSlotBubbles );
} else {
	initNextSlotBubbles();
}
